import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGS, swapLangInPath, useLang } from "@/i18n";

/**
 * Accessible language switcher that preserves the current pathname.
 * Renders a button + popover; keyboard focus + Esc close supported.
 * Persists the last user choice in a cookie so if the visitor returns
 * to a new tab, the preference survives (URL still wins on every load).
 */
export const LanguageSwitcher = ({ variant = "desktop" }) => {
  const { lang, t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const choose = (code) => {
    setOpen(false);
    document.cookie = `iag_lang=${code}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    const target = swapLangInPath(pathname, code);
    navigate(target);
  };

  const isMobile = variant === "mobile";

  return (
    <div ref={rootRef} className={`relative ${isMobile ? "" : "hidden lg:block"}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language_switcher.aria")}
        data-testid={`lang-switcher-${variant}`}
        className={`inline-flex items-center gap-1.5 font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-terracotta transition-colors ${
          isMobile ? "px-1 py-2 text-base tracking-[0.2em]" : ""
        }`}
      >
        <span aria-hidden="true">{current.code.toUpperCase()}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M1 3l4 4 4-4" stroke="currentColor" fill="none" strokeWidth="1.4" />
        </svg>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t("nav.language")}
          data-testid={`lang-switcher-${variant}-menu`}
          className={`absolute z-50 mt-2 min-w-[180px] bg-paper border border-ink/15 shadow-[6px_12px_44px_rgba(27,27,24,0.10)] py-2 ${
            isMobile ? "left-0" : "right-0"
          }`}
        >
          {LANGS.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === lang}>
              <button
                type="button"
                onClick={() => choose(l.code)}
                data-testid={`lang-option-${l.code}`}
                lang={l.htmlLang}
                className={`w-full text-left px-4 py-2 font-body text-sm hover:bg-paper-deep/40 transition-colors ${
                  l.code === lang ? "text-terracotta" : "text-ink"
                }`}
              >
                <span className="uppercase tracking-[0.14em] text-xs mr-2 text-pencil">
                  {l.code}
                </span>
                {l.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
